import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Grid, Typography, FormControlLabel, Checkbox,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Field } from '../../UI';
import {
  findPastasFaseAction, findMotivosBaixaAction,
} from '../../../../store/Global/LoadSelectors/loadSelectors.saga';
import { clearSelectorAction } from '../../../../store/Global/LoadSelectors/loadSelectors.store';
import {
  atualizarFaseProcessoService, validarAntecedentes,
} from '../../../../services/core/processos/processosFaseService';
import { acaoEnviarController as controller } from '../../../../pages/InspecionarProcesso/AcaoEnviar/acaoEnviarController';
import { tipoTextoPadraoTypes } from '../../../../types/tipoTextoPadraoTypes';
import {
  clearErrorField, extractResponseErrorMessage, updateFieldsWithErrors,
} from '../../../../helpers/utils/form.utils';
import { haveExceptionValidations, isBadRequestException } from '../../../../helpers/utils/utils';
import * as C from '../../../theme/colors.constants';
import DialogTextoPadrao from './DialogTextoPadrao';
import DialogReescrever from './DialogReescrever';
import DialogSelecionarAntecedentes from './DialogSelecionarAntecedentes';
import usePodeAtuarNaFase from '../../../hooks/usePodeAtuarNaFase';

const TIPOS_TEXTO_PADRAO = [tipoTextoPadraoTypes.HISTORICO.value, tipoTextoPadraoTypes.DEVOLUCAO.value];
const MINIMO_PALAVRAS_REESCREVER = 5;
const DICA_MINIMO_PALAVRAS = 'São necessárias no mínimo 5 palavras para reescrever o texto.';
const MENSAGEM_ERRO_VALIDAR_ANTECEDENTES = 'Não foi possível validar os antecedentes.';
const MENSAGEM_ERRO_GENERICA = 'Não foi possível enviar o processo para outra fase.';

function contarPalavras(texto) {
  return texto.trim().split(' ').length;
}

// `formFields`/`formPadrao` são sempre planos, só primitivos (produzidos
// pela mesma factory `controller.makeFormFields`) — comparação campo a
// campo é suficiente, sem precisar de uma dependência nova (`lodash`) só
// para isso.
function saoFormFieldsIguais(a, b) {
  return Object.keys(a).every(chave => a[chave] === b[chave]);
}

/**
 * Mudança de Fase e Envio — Etapa 8 do plano `docs/architecture/
 * plano-aw2-1054-aba-historico.md`. Port de `AcaoEnviar.jsx` real para
 * dentro da sidebar da aba Histórico, sempre visível (não mais um painel
 * condicional), no mesmo espírito de `BlocoAnotacao.jsx` (Etapa 5).
 *
 * Decisões de escopo (revisadas 2x nesta feature):
 * 1. Botão "Antecedentes" (pedido explícito do usuário após a 1ª versão
 *    desta etapa, que tinha ficado sem seleção): reintroduz uma seleção de
 *    antecedentes ESCOPADA a esta ação (`antecedentesSelecionados`,
 *    estado local deste componente) — não reabre seleção na grid
 *    somente-leitura da Etapa 4 (`TableAntecedentesAtivos.jsx`, que
 *    continua só consulta) nem reaproveita o Map de seleção de
 *    Unificar/Juntar (Acompanhamento, semântica de ação diferente). A
 *    lista para escolher reaproveita o MESMO `antecedentesAtivos` (zero
 *    fetch novo), então um antecedente em fase Baixa nunca é oferecido
 *    para seleção (já fica de fora da própria lista, por construção).
 * 2. `validarAntecedentes` e a replicação de `atualizarFaseProcessoService`
 *    aplicam-se aos antecedentes SELECIONADOS (mesmo comportamento da tela
 *    real, `checkSubmitHandlerDialog`/`onSubmitHandler`) — pré-filtrados
 *    contra o `antecedentesAtivos` ATUAL antes de qualquer chamada (5ª
 *    camada do padrão "IDOR pré-filtro de domínio" já documentado em
 *    `src/pages/InspecionarProcesso/CLAUDE.md`: nunca confiar que um id
 *    selecionado anteriormente ainda é válido no momento do submit — a
 *    lista pode ter mudado por Unificar/Juntar ou por uma troca de fase
 *    concorrente, sem o processo atual ter mudado).
 * 3. Botões "Texto Padrão"/"Reescrever" adicionados ao campo Texto deste
 *    formulário para paridade com a tela real, reaproveitando os mesmos
 *    sub-dialogs genéricos das Etapas 6/7 (`DialogTextoPadrao.jsx`/
 *    `DialogReescrever.jsx`) — não fazem parte do texto original do plano.
 *
 * `useDispatch`/`useSelector` diretos aqui (não elevados ao container) —
 * mesmo precedente já usado por `DialogEnviarOutraFase.jsx` (AcoesEmMassa)
 * para os mesmos 2 selectors (`pastasDaFase`/`motivosBaixa`): a regra
 * "Redux pertence ao container" documentada em `src/prototipo/CLAUDE.md`
 * se aplica a props que alimentam um componente de apresentação PURO (ex.
 * `GridProcessos.jsx`); este componente já tem lógica de negócio própria
 * e services chamados direto (mesma categoria de `BlocoAnotacao.jsx`).
 */
export default function MudancaFaseEnvio({
  processo, sinalizacaoInfo, antecedentesAtivos, onSucesso, deveResetarCampos,
}) {
  const dispatch = useDispatch();
  const pastasDaFaseOptions = useSelector(states => states.selectors.pastasDaFase.selector);
  const motivosBaixaOptions = useSelector(states => states.selectors.motivosBaixa.selector);

  const idFaseAtual = sinalizacaoInfo?.idFase != null ? Number(sinalizacaoInfo.idFase) : null;
  const idFasePastaAtual = sinalizacaoInfo?.idFasePasta || '';
  const idMotivoBaixaAtual = sinalizacaoInfo?.idMotivoBaixa || '';
  const idNegociadorAtual = sinalizacaoInfo?.idNegociador || '';
  const temAgendamento = Boolean(sinalizacaoInfo?.temAgendamento);
  const permiteCobrancaPresencial = sinalizacaoInfo?.sinalizacaoClienteProcessoQuery?.permiteCobrancaPresencial;

  // AW2-1056 Etapa 1: gate client-side (só UX, sem mensagem de bloqueio —
  // AC pede "desabilitado, não oculto, sem mensagem") por permissão de fase
  // do usuário logado, mesmo padrão já usado em `BlocoAnotacao.jsx` (AW2-1055
  // Etapa 1) — extraído para o hook compartilhado `usePodeAtuarNaFase` por
  // ser a 4ª ocorrência do padrão no módulo. Fail-closed: autorização real
  // permanece 100% no backend (`atualizarFaseProcessoService`).
  const podeEnviarNaFase = usePodeAtuarNaFase(idFaseAtual);

  const [fases, setFases] = useState([]);
  const [faseSelecionada, setFaseSelecionada] = useState(null);
  const [negociadores, setNegociadores] = useState([]);
  const [formFields, setFormFields] = useState(() => controller.makeFormFields({ liberarCompromissos: temAgendamento }));
  const [errorFields, setErrorFields] = useState(controller.makeErrorFields);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmarEnvioAberto, setConfirmarEnvioAberto] = useState(false);
  const [limparAberto, setLimparAberto] = useState(false);
  const [textoPadraoAberto, setTextoPadraoAberto] = useState(false);
  const [reescreverAberto, setReescreverAberto] = useState(false);
  const [antecedentesSelecionados, setAntecedentesSelecionados] = useState([]);
  const [antecedentesDialogAberto, setAntecedentesDialogAberto] = useState(false);

  const sessaoRef = useRef(0);
  const enviandoRef = useRef(false);

  const resetarFormulario = useCallback(() => {
    setFormFields(controller.makeFormFields({ liberarCompromissos: temAgendamento }));
    setErrorFields(controller.makeErrorFields());
    setNegociadores([]);
    setFaseSelecionada(null);
    dispatch(clearSelectorAction('pastasDaFase'));
  }, [dispatch, temAgendamento]);

  // Reset por troca de processo E por troca de `idFaseAtual` — o 2º caso
  // cobre o pós-sucesso desta própria etapa: o container recarrega
  // `sinalizacaoInfo` (via `onSucesso`), `idFaseAtual` muda para o valor
  // novo, e este efeito reage sozinho (mesmo mecanismo da tela real:
  // `AcaoEnviar.jsx` chama `confirmAlertLimpar()` dentro do `.then` do
  // fetch de fases, que é refeito sempre que `idFaseAtual` muda).
  //
  // Etapa 10: `deveResetarCampos` só se aplica quando este efeito dispara
  // por TROCA DE PROCESSO — resetar o formulário/seleção de antecedentes
  // após o SUCESSO do próprio envio (mesmo processo, `idFaseAtual` mudou
  // porque acabamos de mudar a fase dele) é sempre incondicional, nunca
  // opcional: não tem relação com "preservar dado ao trocar de processo",
  // é só o formulário refletindo que a ação foi concluída. `processoAnteriorRef`
  // (distinto do reset em si) é o que permite diferenciar as 2 origens do
  // mesmo efeito.
  //
  // `deveResetarCampos` é lido via ref ("latest ref", mesmo padrão de
  // `usePaginacaoOrdenacaoPorProcesso`), NUNCA como dependência do efeito —
  // o valor muda sozinho pouco depois do mount (a "primeira visita" que o
  // container calcula vira `false` assim que o próprio container re-renderiza
  // por qualquer outro motivo, o que acontece com frequência nesta página).
  // Se fosse dependência, essa mudança sozinha (sem `processo?.id` mudar)
  // reexecutaria o efeito inteiro — fechando dialogs e resetando o
  // formulário de baixo do usuário, mesmo sem nenhuma troca de processo
  // real ter ocorrido.
  const deveResetarCamposRef = useRef(deveResetarCampos);
  deveResetarCamposRef.current = deveResetarCampos;

  const processoAnteriorRef = useRef(processo?.id);
  useEffect(() => {
    const trocouProcesso = processoAnteriorRef.current !== processo?.id;
    processoAnteriorRef.current = processo?.id;

    sessaoRef.current += 1;
    enviandoRef.current = false;
    setEnviando(false);
    setErro('');
    setConfirmarEnvioAberto(false);
    setLimparAberto(false);
    setTextoPadraoAberto(false);
    setReescreverAberto(false);
    setAntecedentesDialogAberto(false);

    if (!trocouProcesso || deveResetarCamposRef.current) {
      resetarFormulario();
      setAntecedentesSelecionados([]);
    } else {
      // Preservar SÓ o texto livre (mesmo espírito de `BlocoAnotacao.jsx`),
      // não o objeto `formFields` inteiro — achado em code review: Fase/
      // Pasta/Motivo/Negociador não são "rascunho" portável entre processos
      // como um texto é. Fases/pastas/motivos válidos variam por processo, e
      // não existe (nem seria simples construir) uma revalidação para essa
      // combinação equivalente à que `antecedentesSelecionados` já tem
      // (reseed contra `antecedentesAtivos` atual) — preservar `idFase` cru
      // arriscaria `podeEnviar` virar `true` sozinho ao trocar de processo,
      // habilitando "Enviar" com uma fase escolhida no contexto ERRADO sem
      // nenhuma interação nova do usuário. `antecedentesSelecionados`
      // continua preservado normalmente (já é revalidado contra o processo
      // atual antes de qualquer submit, ver `onClickEnviar`).
      setFormFields((atual) => {
        const padrao = controller.makeFormFields({ liberarCompromissos: temAgendamento });
        return { ...padrao, texto: atual.texto };
      });
      setErrorFields(controller.makeErrorFields());
      setNegociadores([]);
      setFaseSelecionada(null);
      dispatch(clearSelectorAction('pastasDaFase'));
    }

    const alvo = processo?.id;
    if (!alvo || idFaseAtual == null) {
      setFases([]);
      return undefined;
    }

    let ignore = false;
    controller.buscarFases(permiteCobrancaPresencial, idFaseAtual).then((res) => {
      if (!ignore) setFases(res);
    });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processo?.id, idFaseAtual, permiteCobrancaPresencial]);

  // Reseed (não reset) da seleção quando `antecedentesAtivos` muda por um
  // motivo QUE NÃO é troca de processo/fase — ex.: Unificar/Juntar em
  // Acompanhamento altera a mesma lista `antecedentes` compartilhada, sem o
  // processo em foco mudar. Sem isso, um id removido e depois reintroduzido
  // (mesmo `id`, ex. um Unificar desfeito) reapareceria marcado sem o
  // usuário reselecionar — mesma regra já documentada no CLAUDE.md do
  // módulo para Maps de seleção ("reseedar em `[antecedentes]`, não só
  // `[processo?.id]`"). O pré-filtro em `onClickEnviar` já impede uma
  // mutação real com um id obsoleto; este efeito só evita a seleção
  // "fantasma" ficar visível na UI entre uma mudança e a próxima tentativa
  // de envio.
  useEffect(() => {
    setAntecedentesSelecionados((atual) => {
      if (!antecedentesAtivos) return atual;
      const idsValidos = new Set(antecedentesAtivos.map(a => a.id));
      const filtrado = atual.filter(id => idsValidos.has(id));
      return filtrado.length === atual.length ? atual : filtrado;
    });
  }, [antecedentesAtivos]);

  function onChangeFase(event) {
    const { value: idFase } = event.target;
    const isNotFaseAtual = String(idFaseAtual) !== String(idFase);

    let liberarCompromissos = temAgendamento;
    if (temAgendamento) {
      liberarCompromissos = isNotFaseAtual;
    }

    setFormFields(atual => ({
      ...atual,
      idFase,
      liberarCompromissos,
      liberarRequisicao: isNotFaseAtual,
      idFasePasta: '',
      idMotivoBaixa: '',
      idNegociador: '',
    }));
    setErrorFields(controller.makeErrorFields());

    const novaFaseSelecionada = fases.find(fase => String(fase.id) === String(idFase)) || null;
    setFaseSelecionada(novaFaseSelecionada);

    // Nunca despachar `findPastasFaseAction` com `idFase` vazio — a saga
    // (`findPastasHandler`) tem um bug conhecido que deixa `loading` preso
    // em `true` para sempre nesse caso (documentado em `src/store/CLAUDE.md`).
    if (idFase) {
      const onlyPublico = Number(idFaseAtual) === Number(idFase) ? null : 'S';
      dispatch(findPastasFaseAction(idFase, onlyPublico));
    }

    if (novaFaseSelecionada?.exigeMotivoBaixa) {
      dispatch(findMotivosBaixaAction());
    }

    if (novaFaseSelecionada?.exigeNegociadorExterno) {
      controller.buscarNegociadoresDaFase(idFase).then(setNegociadores);
    } else {
      setNegociadores([]);
    }
  }

  function onChangeCampo(nome) {
    return (event) => {
      const { value } = event.target;
      setFormFields(atual => ({ ...atual, [nome]: value }));
    };
  }

  function onChangeCheckbox(nome) {
    return (event) => {
      const { checked } = event.target;
      setFormFields(atual => ({ ...atual, [nome]: checked }));
    };
  }

  function limparErro(nome) {
    return () => setErrorFields(atual => clearErrorField(atual, nome));
  }

  function onToggleAntecedente(id) {
    setAntecedentesSelecionados(atual => (
      atual.includes(id) ? atual.filter(item => item !== id) : [...atual, id]
    ));
  }

  function onSelecionarTodosAntecedentes(marcarTodos) {
    setAntecedentesSelecionados(marcarTodos ? (antecedentesAtivos || []).map(a => a.id) : []);
  }

  function onCancelarSelecaoAntecedentes() {
    setAntecedentesSelecionados([]);
    setAntecedentesDialogAberto(false);
  }

  const mostrarPasta = pastasDaFaseOptions.length > 0;
  const mostrarMotivo = Boolean(faseSelecionada?.exigeMotivoBaixa) && motivosBaixaOptions.length > 0;
  const mostrarNegociador = Boolean(faseSelecionada?.exigeNegociadorExterno);
  const mostrarRecalcular = Boolean(faseSelecionada?.recalcularValorProcesso)
    && Number(faseSelecionada?.id) !== Number(idFaseAtual);

  const isFaseAtual = Number(idFaseAtual) === Number(formFields.idFase);

  // Mesma fórmula da tela real (`isNotValidForm`, invertida para positivo):
  // só habilita "Enviar" quando pelo menos um campo VISÍVEL realmente
  // diverge do valor atual do processo — não basta ter selecionado uma
  // Fase, se ela for a mesma que já está ativa e nenhum campo dependente
  // (Pasta/Motivo/Negociador) também mudou.
  const podeEnviar = useMemo(() => {
    const isDifferentFase = String(formFields.idFase) !== String(idFaseAtual);
    const contemFase = Boolean(formFields.idFase) && isDifferentFase;

    const isDifferentPasta = String(formFields.idFasePasta) !== String(idFasePastaAtual);
    const contemPasta = mostrarPasta && isDifferentPasta;

    const isDifferentMotivo = String(formFields.idMotivoBaixa) !== String(idMotivoBaixaAtual);
    const contemMotivo = mostrarMotivo && Boolean(formFields.idMotivoBaixa) && isDifferentMotivo;

    const isDifferentNegociador = String(formFields.idNegociador) !== String(idNegociadorAtual);
    const contemNegociador = negociadores.length > 0 && Boolean(formFields.idNegociador) && isDifferentNegociador;

    return contemFase || contemPasta || contemMotivo || contemNegociador;
  }, [
    formFields, idFaseAtual, idFasePastaAtual, idMotivoBaixaAtual, idNegociadorAtual,
    mostrarPasta, mostrarMotivo, negociadores.length,
  ]);

  async function enviarMudancaFase(alvo, sessaoAlvo, antecedentesValidos) {
    try {
      await atualizarFaseProcessoService(alvo, { ...formFields });
      // Replicação nos antecedentes selecionados (mesmo comportamento da
      // tela real) — `Promise.allSettled`, não `forEach`/`Promise.all`: a
      // tela real dispara essas chamadas sem aguardar e sem tratar erro
      // individual (fire-and-forget); aqui aguardamos para não deixar uma
      // rejeição sem handler (`allSettled` nunca rejeita), mas sem mudar o
      // comportamento observável — nenhum erro por antecedente é
      // reportado, mesma paridade da tela real.
      if (antecedentesValidos.length > 0) {
        await Promise.allSettled(
          antecedentesValidos.map(id => atualizarFaseProcessoService(id, { ...formFields })),
        );
      }
      if (sessaoRef.current !== sessaoAlvo) return;
      if (onSucesso) onSucesso();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) return;
      if (isBadRequestException(exception) && haveExceptionValidations(exception)) {
        const [atualizados] = updateFieldsWithErrors(exception, errorFields);
        setErrorFields({ ...atualizados });
      } else {
        setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
      }
    }
  }

  // Guarda o contexto (`alvo`/`sessaoAlvo`) capturado no momento em que o
  // dialog de confirmação é ABERTO — `onConfirmarEnvio` reaplica esse
  // mesmo contexto, nunca relê `processo?.id`/`sessaoRef.current` no
  // instante do clique em "Confirmar". Sem isso, o único motivo do fluxo
  // ser seguro seria o backdrop do MUI `Dialog` impedir clicar em outra
  // linha da grid enquanto o dialog está aberto — um invariante implícito
  // de biblioteca, não estrutural (achado em code review).
  const confirmarEnvioContextoRef = useRef(null);

  async function onClickEnviar() {
    const alvo = processo?.id;
    // Fail-closed enquanto `antecedentesAtivos` ainda não carregou (`null`,
    // distinto de `[]`): sem o dado, não dá para confirmar que não há
    // antecedente com acordo ativo — nunca tratar "ainda não sei" como
    // "não há antecedentes" (isso pularia o aviso de confirmação em
    // silêncio, achado em code review).
    if (!alvo || enviandoRef.current || !podeEnviar || antecedentesAtivos == null || !podeEnviarNaFase) return;

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErro('');

    // Revalida a seleção contra a lista ATUAL de antecedentes ativos antes
    // de qualquer chamada — um id selecionado antes pode ter deixado de
    // ser válido nesse meio-tempo (mudou de fase, foi unificado/juntado em
    // Acompanhamento) sem o processo em foco ter mudado. Nunca confiar que
    // ids preservados ainda pertencem à lista atual.
    const antecedentesValidos = antecedentesSelecionados.filter(
      id => antecedentesAtivos.some(a => a.id === id),
    );

    try {
      const { data: antecedentesOk } = await validarAntecedentes(antecedentesValidos, Number(formFields.idFase));
      if (sessaoRef.current !== sessaoAlvo) return;
      if (antecedentesOk) {
        await enviarMudancaFase(alvo, sessaoAlvo, antecedentesValidos);
      } else {
        confirmarEnvioContextoRef.current = { alvo, sessaoAlvo, antecedentesValidos };
        setConfirmarEnvioAberto(true);
      }
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) return;
      setErro(MENSAGEM_ERRO_VALIDAR_ANTECEDENTES);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  }

  async function onConfirmarEnvio() {
    const contexto = confirmarEnvioContextoRef.current;
    setConfirmarEnvioAberto(false);
    if (!contexto || enviandoRef.current || sessaoRef.current !== contexto.sessaoAlvo) return;

    const { alvo, sessaoAlvo } = contexto;
    // Revalida de novo aqui, não só reusa o que foi filtrado na abertura do
    // dialog — o usuário pode levar um tempo para clicar "Confirmar", e
    // `antecedentesAtivos` pode ter mudado nesse meio-tempo (Unificar/Juntar
    // em Acompanhamento, sem o processo em foco mudar).
    const antecedentesValidos = contexto.antecedentesValidos.filter(
      id => (antecedentesAtivos || []).some(a => a.id === id),
    );
    enviandoRef.current = true;
    setEnviando(true);
    try {
      await enviarMudancaFase(alvo, sessaoAlvo, antecedentesValidos);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  }

  const formPadrao = useMemo(
    () => controller.makeFormFields({ liberarCompromissos: temAgendamento }),
    [temAgendamento],
  );
  const isDisabledLimpar = saoFormFieldsIguais(formFields, formPadrao);

  function onConfirmarTextoPadrao(textoPadrao) {
    setFormFields(atual => ({
      ...atual,
      texto: atual.texto.trim().length > 0 ? `${atual.texto} ${textoPadrao}` : textoPadrao,
    }));
  }

  function onAceitarReescrever(novoTexto) {
    setFormFields(atual => ({ ...atual, texto: novoTexto }));
  }

  const podeReescrever = contarPalavras(formFields.texto) >= MINIMO_PALAVRAS_REESCREVER;

  return (
    <Box sx={{ mt: 2.5 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1,
      }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy }}>
          Mudança de Fase e Envio
        </Typography>
        <Button size="small" disabled={isDisabledLimpar || enviando} onClick={() => setLimparAberto(true)}>
          Limpar
        </Button>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={mostrarPasta ? 6 : 12}>
          <Field
            tipo="select"
            label="Fase*"
            options={fases}
            value={formFields.idFase}
            onChange={onChangeFase}
            onFocus={limparErro('idFase')}
            error={errorFields.idFase.error}
            helperText={errorFields.idFase.errorMessage}
            disabled={enviando}
          />
        </Grid>
        {mostrarPasta && (
          <Grid item xs={6}>
            <Field
              tipo="select"
              label="Pasta"
              options={pastasDaFaseOptions}
              value={formFields.idFasePasta}
              onChange={onChangeCampo('idFasePasta')}
              disabled={enviando}
            />
          </Grid>
        )}
        {mostrarMotivo && (
          <Grid item xs={12}>
            <Field
              tipo="select"
              label="Motivo*"
              options={motivosBaixaOptions}
              value={formFields.idMotivoBaixa}
              onChange={onChangeCampo('idMotivoBaixa')}
              onFocus={limparErro('idMotivoBaixa')}
              error={errorFields.idMotivoBaixa.error}
              helperText={errorFields.idMotivoBaixa.errorMessage}
              disabled={enviando}
            />
          </Grid>
        )}
        {mostrarNegociador && (
          <Grid item xs={12}>
            <Field
              tipo="select"
              label="Negociador*"
              options={negociadores}
              value={formFields.idNegociador}
              onChange={onChangeCampo('idNegociador')}
              onFocus={limparErro('idNegociador')}
              error={errorFields.idNegociador.error}
              helperText={errorFields.idNegociador.errorMessage}
              disabled={enviando}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            disabled={isFaseAtual || enviando}
            control={<Checkbox checked={formFields.liberarRequisicao} />}
            label="Liberar Requisição do Processo"
            onChange={onChangeCheckbox('liberarRequisicao')}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            disabled={isFaseAtual || !temAgendamento || enviando}
            control={<Checkbox checked={formFields.liberarCompromissos} />}
            label="Liberar compromisso da Agenda do Processo"
            onChange={onChangeCheckbox('liberarCompromissos')}
          />
        </Grid>
        {mostrarRecalcular && (
          <Grid item xs={12}>
            <FormControlLabel
              disabled={enviando}
              control={<Checkbox checked={formFields.recalcularValorProcesso} />}
              label="Recalcular o valor do Processo"
              onChange={onChangeCheckbox('recalcularValorProcesso')}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Field
            label="Texto"
            multiline
            minRows={4}
            value={formFields.texto}
            onChange={onChangeCampo('texto')}
            disabled={enviando}
          />
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
            onClick={() => setTextoPadraoAberto(true)}
            disabled={enviando}
          >
            Texto Padrão
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, ml: 1 }}
            onClick={() => setReescreverAberto(true)}
            disabled={enviando || !podeReescrever}
            title={DICA_MINIMO_PALAVRAS}
          >
            Reescrever
          </Button>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setAntecedentesDialogAberto(true)}
            disabled={enviando || antecedentesAtivos == null}
          >
            Antecedentes
          </Button>
          <Button
            variant="contained"
            onClick={onClickEnviar}
            disabled={!podeEnviar || enviando || antecedentesAtivos == null || !podeEnviarNaFase}
          >
            Enviar
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErro('')}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>

      <Dialog open={confirmarEnvioAberto} onClose={() => setConfirmarEnvioAberto(false)}>
        <DialogTitle>Confirmar envio</DialogTitle>
        <DialogContent>
          Há processo antecedente cujo acordo se encontra ativo. Portanto, a mudança de fase poderá
          causar problemas. Confirma o envio do processo para outra fase?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarEnvioAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={onConfirmarEnvio} disabled={enviando}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={limparAberto} onClose={() => setLimparAberto(false)}>
        <DialogTitle>Limpar campos</DialogTitle>
        <DialogContent>Tem certeza de que deseja descartar essas informações?</DialogContent>
        <DialogActions>
          <Button onClick={() => setLimparAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={enviando}
            onClick={() => { resetarFormulario(); setErro(''); setLimparAberto(false); }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <DialogTextoPadrao
        open={textoPadraoAberto}
        tipos={TIPOS_TEXTO_PADRAO}
        onClose={() => setTextoPadraoAberto(false)}
        onConfirmar={onConfirmarTextoPadrao}
      />
      <DialogReescrever
        open={reescreverAberto}
        texto={formFields.texto}
        onClose={() => setReescreverAberto(false)}
        onAceitar={onAceitarReescrever}
      />

      <DialogSelecionarAntecedentes
        open={antecedentesDialogAberto}
        antecedentes={antecedentesAtivos}
        selecionados={antecedentesSelecionados}
        onToggle={onToggleAntecedente}
        onSelecionarTodos={onSelecionarTodosAntecedentes}
        onClose={() => setAntecedentesDialogAberto(false)}
        onCancelar={onCancelarSelecaoAntecedentes}
      />
    </Box>
  );
}

MudancaFaseEnvio.propTypes = {
  processo: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  sinalizacaoInfo: PropTypes.shape({
    idFase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idFasePasta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idMotivoBaixa: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idNegociador: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    temAgendamento: PropTypes.bool,
    sinalizacaoClienteProcessoQuery: PropTypes.shape({
      permiteCobrancaPresencial: PropTypes.bool,
    }),
  }),
  antecedentesAtivos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    numero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    negociador: PropTypes.string,
    fase: PropTypes.string,
  })),
  onSucesso: PropTypes.func,
  deveResetarCampos: PropTypes.bool.isRequired,
};
MudancaFaseEnvio.defaultProps = {
  processo: null,
  sinalizacaoInfo: null,
  antecedentesAtivos: null,
  onSucesso: null,
};
